import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Clock, Check, X, RotateCcw, Trophy } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import Header from "../components/Header";
import TutorialVideoSection from "../components/TutorialVideoSection";
import { API } from "../App";
import { authFetch } from "../lib/auth";
import { localStartOfWeek, localStartOfMonth } from "../lib/timeWindow";
import PersonalBestBadge from "../components/PersonalBestBadge";
import AchievementUnlocked from "../components/AchievementUnlocked";
import RatingSummary from "../components/RatingSummary";
import { targetPaceLine } from "../lib/targetPace";
import { toast } from "sonner";

const PHASES = {
  CONFIG: "config",
  MEMORIZE: "memorize",
  RECALL: "recall",
  RESULTS: "results",
};

const NumberTraining = ({ user }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState(PHASES.CONFIG);
  const [digitCount, setDigitCount] = useState(40);
  const [memorizeTime, setMemorizeTime] = useState(60);
  const [sessionData, setSessionData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [memorizeSeconds, setMemorizeSeconds] = useState(0);
  const timerRef = useRef(null);
  const recallTimerRef = useRef(null);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recallTimerRef.current) clearInterval(recallTimerRef.current);
    };
  }, []);

  const startTraining = async () => {
    setLoading(true);
    try {
      const response = await authFetch(`${API}/training/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "numbers",
          items_count: digitCount,
        }),
      });

      if (!response.ok) throw new Error("Failed to start training");

      const data = await response.json();
      setSessionData(data);
      setUserAnswers(data.items.map(() => ""));
      setTimeLeft(memorizeTime);
      setPhase(PHASES.MEMORIZE);

      // Start countdown timer
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setMemorizeSeconds(memorizeTime);
            setPhase(PHASES.RECALL);
            setElapsedTime(0);
            // Start recall timer
            recallTimerRef.current = setInterval(() => {
              setElapsedTime((t) => t + 1);
            }, 1000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      toast.error("Failed to start training");
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (index, value) => {
    // Only allow digits
    const sanitized = value.replace(/\D/g, "");
    const newAnswers = [...userAnswers];
    newAnswers[index] = sanitized;
    setUserAnswers(newAnswers);
  };

  const submitAnswers = async () => {
    if (recallTimerRef.current) clearInterval(recallTimerRef.current);
    setLoading(true);

    try {
      const response = await authFetch(`${API}/training/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionData.session_id,
          answers: userAnswers,
          time_taken_seconds: elapsedTime,
          memorize_seconds: memorizeSeconds,
          start_of_week_iso: localStartOfWeek(),
          start_of_month_iso: localStartOfMonth(),
        }),
      });

      if (!response.ok) throw new Error("Failed to submit");

      const data = await response.json();
      setResults(data);
      setPhase(PHASES.RESULTS);
    } catch (error) {
      toast.error("Failed to submit answers");
    } finally {
      setLoading(false);
    }
  };

  const resetTraining = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (recallTimerRef.current) clearInterval(recallTimerRef.current);
    setPhase(PHASES.CONFIG);
    setSessionData(null);
    setUserAnswers([]);
    setResults(null);
    setElapsedTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen bg-background" data-testid="number-training-page">
      <Header user={user} />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <AnimatePresence mode="wait">
          {/* Config Phase */}
          {phase === PHASES.CONFIG && (
            <motion.div
              key="config"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl">Configure Training</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  {/* Digit Count */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-lg">Number of Digits</Label>
                      <span className="font-mono text-2xl text-lime-400">{digitCount}</span>
                    </div>
                    <Slider
                      value={[digitCount]}
                      onValueChange={(v) => setDigitCount(v[0])}
                      min={20}
                      max={200}
                      step={20}
                      className="w-full"
                      data-testid="digit-count-slider"
                    />
                    <p className="text-sm text-muted-foreground">
                      {Math.ceil(digitCount / 20)} line(s) × 20 digits per line
                    </p>
                  </div>

                  {/* Memorize Time */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label className="text-lg">Memorization Time</Label>
                      <span className="font-mono text-2xl text-lime-400">{memorizeTime}s</span>
                    </div>
                    <Slider
                      value={[memorizeTime]}
                      onValueChange={(v) => setMemorizeTime(v[0])}
                      min={30}
                      max={300}
                      step={30}
                      className="w-full"
                      data-testid="memorize-time-slider"
                    />
                    <p className="text-xs text-muted-foreground" data-testid="target-pace-line">
                      {targetPaceLine("numbers", digitCount, "digits")}
                    </p>
                  </div>

                  <Button
                    onClick={startTraining}
                    disabled={loading}
                    className="w-full bg-lime-500 text-black py-6 text-lg btn-hover hover:bg-lime-400"
                    data-testid="start-training-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Play className="w-5 h-5 mr-2" />
                        Start Training
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
              <TutorialVideoSection section="numbers" />
            </motion.div>
          )}

          {/* Memorize Phase */}
          {phase === PHASES.MEMORIZE && sessionData && (
            <motion.div
              key="memorize"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-card border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-2xl">Memorize</CardTitle>
                  <div className="flex items-center gap-2 text-lime-400">
                    <Clock className="w-5 h-5" />
                    <span className="font-mono text-2xl">{formatTime(timeLeft)}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4 mb-8">
                    {sessionData.items.map((line, index) => (
                      <div
                        key={index}
                        className="p-4 bg-muted border border-border"
                        data-testid={`memorize-line-${index}`}
                      >
                        <div className="font-mono text-2xl md:text-3xl tracking-widest text-center">
                          {line}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={() => {
                      clearInterval(timerRef.current);
                      setMemorizeSeconds(memorizeTime - timeLeft);
                      setPhase(PHASES.RECALL);
                      setElapsedTime(0);
                      recallTimerRef.current = setInterval(() => {
                        setElapsedTime((t) => t + 1);
                      }, 1000);
                    }}
                    variant="outline"
                    className="w-full py-6"
                    data-testid="skip-to-recall-btn"
                  >
                    I'm Ready - Skip to Recall
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Recall Phase */}
          {phase === PHASES.RECALL && sessionData && (
            <motion.div
              key="recall"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-card border-border">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="font-heading text-2xl">Recall</CardTitle>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="w-5 h-5" />
                    <span className="font-mono text-xl">{formatTime(elapsedTime)}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-6">
                    Enter the digits you memorized. Each line should have 20 digits.
                  </p>

                  <div className="space-y-4 mb-8">
                    {userAnswers.map((answer, index) => (
                      <div key={index} className="space-y-2">
                        <Label className="text-sm text-muted-foreground">
                          Line {index + 1}
                        </Label>
                        <Input
                          value={answer}
                          onChange={(e) => handleAnswerChange(index, e.target.value)}
                          placeholder="Enter 20 digits..."
                          className="font-mono text-xl tracking-widest digit-input bg-muted border-border"
                          maxLength={20}
                          data-testid={`recall-input-${index}`}
                        />
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={submitAnswers}
                    disabled={loading}
                    className="w-full bg-lime-500 text-black py-6 text-lg btn-hover hover:bg-lime-400"
                    data-testid="submit-answers-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Check className="w-5 h-5 mr-2" />
                        Submit Answers
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Results Phase */}
          {phase === PHASES.RESULTS && results && (
            <motion.div
              key="results"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-2">
                    <Trophy className="w-6 h-6 text-lime-400" />
                    Results
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* NPB badge */}
                  <div className="text-center">
                    <PersonalBestBadge results={results} />
                  </div>
                  <AchievementUnlocked results={results} />
                  {/* Rating */}
                  <RatingSummary
                    results={results}
                    mode="numbers"
                    itemsCount={digitCount}
                    memorizeAllowanceSeconds={memorizeTime}
                    accent="text-lime-400"
                  />

                  {/* Time Breakdown */}
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    <div className="text-center p-3 bg-muted border border-border rounded">
                      <div className="text-xl font-mono text-lime-400">{formatTime(memorizeSeconds)}</div>
                      <p className="text-xs text-muted-foreground mt-1">Memorisation</p>
                    </div>
                    <div className="text-center p-3 bg-muted border border-border rounded">
                      <div className="text-xl font-mono text-blue-400">{formatTime(Math.round(results.time_taken_seconds))}</div>
                      <p className="text-xs text-muted-foreground mt-1">Recall</p>
                    </div>
                  </div>

                  {/* Comparison */}
                  <div className="space-y-4 mb-8">
                    <h3 className="font-semibold text-lg">Comparison</h3>
                    {results.original_items.map((original, index) => {
                      const userAnswer = results.user_answers[index] || "";
                      return (
                        <div key={index} className="space-y-2">
                          <Label className="text-sm text-muted-foreground">
                            Line {index + 1}
                          </Label>
                          <div className="grid grid-cols-1 gap-2">
                            <div className="p-3 bg-muted border border-border">
                              <span className="text-xs text-muted-foreground block mb-1">
                                Original
                              </span>
                              <div className="font-mono text-lg tracking-widest">
                                {original.split("").map((char, i) => (
                                  <span
                                    key={i}
                                    className={
                                      userAnswer[i] === char
                                        ? "text-green-500"
                                        : "text-foreground"
                                    }
                                  >
                                    {char}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div className="p-3 bg-muted border border-border">
                              <span className="text-xs text-muted-foreground block mb-1">
                                Your Answer
                              </span>
                              <div className="font-mono text-lg tracking-widest">
                                {userAnswer ? (
                                  userAnswer.split("").map((char, i) => (
                                    <span
                                      key={i}
                                      className={
                                        original[i] === char
                                          ? "text-green-500"
                                          : "text-destructive"
                                      }
                                    >
                                      {char}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-muted-foreground italic">
                                    No answer
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-4">
                    <Button
                      onClick={resetTraining}
                      className="flex-1 bg-lime-500 text-black py-6 btn-hover hover:bg-lime-400"
                      data-testid="try-again-btn"
                    >
                      <RotateCcw className="w-5 h-5 mr-2" />
                      Try Again
                    </Button>
                    <Button
                      onClick={() => navigate("/leaderboard")}
                      variant="outline"
                      className="flex-1 py-6"
                      data-testid="view-leaderboard-btn"
                    >
                      <Trophy className="w-5 h-5 mr-2" />
                      Leaderboard
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default NumberTraining;
